import { Platform } from 'react-native';
import * as Sharing from 'expo-sharing';
import { printToFileAsync } from 'expo-print';
import { generateReportHTML } from '../../../../../common/templates/moduleReportTemplate';
import logo from '../../../../../assets/base64/snext-logo';
import log from '../../../../../common/utils/logger';

// Remove the global import - we'll load it only when needed
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let html2pdf: any = null;

/**
 * Lazy load html2pdf only when needed
 * @returns {Promise<any>} html2pdf module
 */
const loadHtml2Pdf = async () => {
  if (!html2pdf && Platform.OS === 'web') {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const module = await import('html2pdf.js' as any);
      html2pdf = module.default;
    } catch (error) {
      log.error('Failed to load html2pdf:', error);
      throw new Error('PDF generation library could not be loaded');
    }
  }
  return html2pdf;
};

interface GeneratePDFProps {
  userName: string;
  numCourses: number;
  numModules: number;
  finishedModules: number;
  pendingModules: number;
  progressPercent: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  modulesData: any;
  setCreatingPDF: (loading: boolean) => void;
  showToastMessage: (type: string, message: string, title?: string, position?: string, duration?: number) => void;
}

export const generatePDF = async ({
  userName,
  numCourses,
  numModules,
  finishedModules,
  pendingModules,
  progressPercent,
  modulesData,
  setCreatingPDF,
  showToastMessage,
}: GeneratePDFProps) => {
  try {
    setCreatingPDF(true);
    const htmlContent = generateReportHTML({
      logo,
      userName,
      numCourses,
      numModules,
      finishedModules,
      pendingModules,
      progressPercent,
      modulesData,
    });

    if (Platform.OS === 'web') {
      // Load html2pdf only when we actually need it
      const html2pdfInstance = await loadHtml2Pdf();

      if (!html2pdfInstance) {
        throw new Error('PDF generation is not available');
      }

      const opt = {
        margin: [0, 0, 0, 0],
        filename: `${userName}_report.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          scrollY: -window.scrollY,
          windowWidth: 794,
          windowHeight: 1123,
          removeContainer: true,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
          compress: true,
          putTotalPages: true,
          precision: 16,
        },
        pagebreak: {
          mode: ['css', 'legacy'],
          before: '.page',
          avoid: '.last-page',
          allowTags: ['DIV', 'TABLE'],
        },
      };

      try {
        await html2pdfInstance().set(opt).from(htmlContent).save();
      } catch (pdfError) {
        // console.error('PDF Generation Error:', pdfError);
        throw new Error('Failed to generate the report');
      }
    } else {
      try {
        const { uri } = await printToFileAsync({
          html: htmlContent,
          base64: false,
          width: 794,
          height: 1123,
        });

        if (await Sharing.isAvailableAsync()) {
          await Sharing.shareAsync(uri);
        } else {
          throw new Error('Sharing is not available on this platform');
        }
      } catch (printError) {
        // console.error('Print/Share Error:', printError);
        showToastMessage('error', 'Failed to generate or share the report', 'Error', 'bottom', 4000);
      }
    }
  } catch (error: unknown) {
    // console.error('Report Generation Error:', error);
    showToastMessage('error', 'An unexpected error occurred', 'Error', 'bottom', 4000);
  } finally {
    setCreatingPDF(false);
  }
};
